import { useCallback, useEffect, useMemo } from "react"

import * as turf from "@turf/turf"

import { createMapIcon, Source, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"

import { useHorizontalSignsQueries } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"

export function HorizontalSigningMultiCampaignsSource(props) {

    const {
        campaigns,
        getIconComponent,
        iconOffset = 0,
        id,
        children,
        hoveredCampaignId,
        hoveredChildren,
        ...otherProps
    } = props

    const theme = useTheme()
    const { getMapRef } = useMapDispatchContext()
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()

    const campaignsQueries = useHorizontalSignsQueries(
        (campaigns || []).map(campaign => ({
            queryParameters: {
                as_geojson: true,
                campaign: campaign.id,
                ...campaign.queryParameters,
            },
            config: {
                enabled: true,
            },
        })),
        {
            filterBySubnetworks: false,
        },
    )

    const sanitizedData = useMemo(() => {
        const sanitizedFeaturesById = new Map()

        campaignsQueries.forEach((query, index) => {
            const campaign = campaigns?.[index]
            if (!query.data || !campaign) {
                return
            }

            query.data.features.forEach(feature => {
                const existingFeature = sanitizedFeaturesById.get(feature.id)
                if (existingFeature) {
                    existingFeature.properties.campaign_ids.push(campaign.id)
                    return
                }

                const disabled = !feature.properties.has_access ||
                    (selectedNetwork.children?.length && !feature.properties.sub_network_slugs.some(slug => selectedSubNetworkSlugs.includes(slug)))
                const color = disabled ? `${theme.palette["common/gray_states"].dark}80` : campaign.color

                sanitizedFeaturesById.set(feature.id, {
                    id: feature.id,
                    geometry: feature.geometry,
                    properties: {
                        ...feature.properties,
                        campaign_ids: [campaign.id],
                        campaign_type: campaign.campaignType,
                        color,
                        disabled,
                        icon: `${campaign.campaignType}-${color}`,
                        linearLocation: feature.linear_location,
                        uuid: feature.id,
                    },
                })
            })
        })

        return turf.featureCollection([...sanitizedFeaturesById.values()])
    }, [campaignsQueries, campaigns, selectedNetwork, selectedSubNetworkSlugs, theme])

    const clusteredData = useMemo(() => {
        if (!hoveredCampaignId) {
            return sanitizedData
        }
        return turf.featureCollection(
            sanitizedData.features.filter(feature => !feature.properties.campaign_ids.some(id => id === hoveredCampaignId)),
        )
    }, [sanitizedData, hoveredCampaignId])

    const hoveredData = useMemo(() => {
        if (!hoveredCampaignId) {
            return turf.featureCollection([])
        }
        return turf.featureCollection(
            sanitizedData.features
                .filter(feature => feature.properties.campaign_ids.some(id => id === hoveredCampaignId))
                .map(feature => {
                    const hoveredCampaign = campaigns?.find(campaign => campaign.id === hoveredCampaignId)
                    const color = feature.properties.disabled ? feature.properties.color : hoveredCampaign?.color || feature.properties.color
                    return {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            color,
                            icon: feature.properties.disabled ? feature.properties.icon : `${hoveredCampaign?.campaignType}-${color}`,
                        },
                    }
                }),
        )
    }, [sanitizedData, campaigns, hoveredCampaignId])

    const createIcon = useCallback(async (type, map, mapTheme, color) => {
        const icon = getIconComponent?.(type)
        if (!icon) {
            return
        }
        return createMapIcon(type, map, mapTheme, iconOffset, color, { icon })
    }, [getIconComponent, iconOffset])

    useEffect(() => {
        const map = getMapRef()?.getMap()

        if (!map || !theme) {
            return undefined
        }

        if (!window.generatedIcons) {
            window.generatedIcons = {}
        }
        sanitizedData?.features?.forEach(feature => {
            const iconId = `${feature.properties.campaign_type}-${feature.properties.color}`
            if (!window.generatedIcons || !window.generatedIcons[iconId]) {
                createIcon(feature.properties.campaign_type, map, theme, feature.properties.color)
            }
        })

        const loadIcon = (e) => {
            const [type, color] = e.id.split("-")
            if (!type) {
                return
            }

            if (!window.generatedIcons) {
                window.generatedIcons = {}
            }

            if (window.generatedIcons[`${type}-${color}`]) {
                map.loadImage(
                    window.generatedIcons[`${type}-${color}`],
                    (error, icon) => {
                        if (error) {
                            return console.error(error)
                        }

                        if (!map.hasImage(e.id)) {
                            map.addImage(e.id, icon)
                        }
                    },
                )
            } else {
                createIcon(type, map, theme, color)
            }
        }

        map?.on("styleimagemissing", loadIcon)

        return () => map?.off("styleimagemissing", loadIcon)

    }, [createIcon, sanitizedData, getMapRef, theme])

    return (
        <>
            <Source {...otherProps} id={id} type="geojson" data={clusteredData} tolerance={.001}>
                {children}
            </Source>
            <Source id={`${id}-hovered`} type="geojson" data={hoveredData} tolerance={.001} cluster={false}>
                {hoveredChildren}
            </Source>
        </>
    )
}
